
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/context/AuthContext';
import { getApiUrl, API_ENDPOINTS } from '@/utils/api-config';
import { apiGet } from '@/utils/api-interceptor';
import { errorMessage } from '@/lib/errorMessage';
import { isRecord } from '@/lib/records';

export interface BusinessItem {
  name: string | null;
  domain: string;
  plan: string;
  status: string;
  admins: number;
  agents: number;
  created: string;
  id?: string;
}
export interface BusinessListData {
  results: BusinessItem[];
  next?: string | null;
  previous?: string | null;
  prev?: string | null;
  count?: number;
}

export interface BusinessResponse {
  message?: string;
  data?: BusinessListData;
  next?: string | null;
  previous?: string | null;
  prev?: string | null;
  results?: BusinessItem[];
  count?: number;
}

export interface BusinessListResult {
  items: BusinessItem[];
  next: string | null;
  previous: string | null;
  count: number;
}

interface BusinessQueryParams {
  search?: string;
  plan?: string;
  status?: string;
  pageUrl?: string | null;
  enabled?: boolean;
  retainPreviousPage?: boolean;
}
export interface ConversationVolumeDatum {
  week: string;
  no_of_conversations: number | string;
  no_of_tokens?: number | string | null;
}

type CostVsSummaryPeriodKey = 'day' | 'month' | 'year';

export type CostVsSubscriptionViewKey = string;

export interface CostVsSubscriptionTotals {
  operational_cost?: number | string | null;
  subscription?: number | string | null;
  difference?: number | string | null;
}

export interface CostVsSubscriptionView {
  key?: string;
  label?: string;
  labels?: string[];
  operational_cost?: Array<number | string | null>;
  subscription?: Array<number | string | null>;
  totals?: CostVsSubscriptionTotals;
}

export interface CostVsSubscriptionSummary {
  operational_cost?: Partial<Record<CostVsSummaryPeriodKey, number | string | null>>;
  subscription?: Partial<Record<CostVsSummaryPeriodKey, number | string | null>>;
  difference?: Partial<Record<CostVsSummaryPeriodKey, number | string | null>>;
}

export interface CostVsSubscriptionDetails {
  billing_cycle?: string;
  current_period_amount?: number | string | null;
  normalized_monthly_amount?: number | string | null;
}

export interface CostVsSubscriptionData {
  basis?: string;
  default_view?: string;
  billing_cycle?: string;
  current_period_amount?: number | string | null;
  summary?: CostVsSubscriptionSummary;
  views?: Record<CostVsSubscriptionViewKey, CostVsSubscriptionView> | CostVsSubscriptionView[] | null;
  subscription_details?: CostVsSubscriptionDetails;
}

export interface BusinessDetailData {
  business_info: {
    name: string;
    email: string;
    phone: string;
    domain: string;
    account_created: string;
    status: "Enabled" | "Disabled";
    sector_activity?: string;
    company_size?: string;
  };
  subscription: {
    status: string;
    plan: string;
    billing_cycle: string;
    next_billing_date: string;
  };
  account_statistics: {
    admins: number;
    agents: number;
    conversations: number;
    documents: number;
  };
  administrators: Administrator[];
  agents: Agent[];
  team: Team[];
  actions: {
    view_usage_analytics: string;
    manage_subscription: string;
  };
  weekwise_conversation_volume: ConversationVolumeDatum[];
  recent_activities: RecentActivities;
  transaction_history: unknown[];
  llm_costs?: {
    agent_cost?: Record<string, number | string | null>;
    llm_cost?: Record<string, number | string | null>;
  };
  cost_vs_subscription_statistics?: CostVsSubscriptionData | string | null;
}

export interface RecentActivities {
 last_login: string;
 agent_updated: string;
 subscription_changed: string;
}

export interface BusinessDetailResponse {
  message: string;
  data: BusinessDetailData;
  status: string;
  permissions: string[];
}

export interface Administrator {
  id: number;
  name: string;
  email: string;
  role: string;
  last_active: string;
}

export interface AgentKnowledgeSource {
  title?: string | null;
  extracted_characters?: number | string | null;
  type?: string | null;
}

export interface Agent {
  id: number;
  name: string;
  agentType: string;
  status: string;
  conversations: number;
  agent_category?: string | null;
  knowledge_sources?: AgentKnowledgeSource[] | null;
}

export interface Team {
  id: number;
  username: string;
  email: string;
  user_role: number;
  team_role: string | number | null;
}

// Hook for listing businesses
export const useBusinesses = (params: BusinessQueryParams = {}) => {
  const { isAuthenticated } = useAuth();
  const { search, plan, status, pageUrl, enabled = true, retainPreviousPage = false } = params;

  return useQuery({
    queryKey: ['businesses', { search, plan, status, pageUrl }],
    placeholderData: retainPreviousPage
      ? (previousData, previousQuery) => {
          const previous = previousQuery?.queryKey[1];
          if (
            pageUrl &&
            previous &&
            typeof previous === 'object' &&
            'search' in previous && previous.search === search &&
            'plan' in previous && previous.plan === plan &&
            'status' in previous && previous.status === status
          ) {
            return previousData;
          }
          return undefined;
        }
      : undefined,
    queryFn: async (): Promise<BusinessListResult> => {
      const requestUrl = (() => {
        if (pageUrl) {
          return pageUrl.startsWith('http') ? pageUrl : getApiUrl(pageUrl);
        }

        const query = new URLSearchParams();
        if (search && search.trim().length > 0) {
          query.set('search', search.trim());
        }
        if (plan && plan !== 'all') {
          query.set('plan', plan);
        }
        if (status && status !== 'all') {
          query.set('status', status);
        }

        const queryString = query.toString();
        return `${getApiUrl(API_ENDPOINTS.ADMIN_BUSINESSES)}${queryString ? `?${queryString}` : ''}`;
      })();

      const response = await apiGet(requestUrl);

      if (!response.ok) {
        let serverMessage = 'Failed to fetch businesses';
        try {
          const errorData = await response.json();
          if (errorData?.error) {
            if (typeof errorData.error === 'string') {
              serverMessage = errorData.error;
            } else if (typeof errorData.error?.message === 'string') {
              serverMessage = errorData.error.message;
            }
          } else if (typeof errorData?.message === 'string') {
            serverMessage = errorData.message;
          }
        } catch (parseError) {
          console.error('Error parsing businesses API error response:', parseError);
        }

        throw new Error(serverMessage);
      }

      const data: BusinessResponse = await response.json();

      const payload: BusinessListData = data.data ?? {
        results: data.results ?? [],
        next: data.next ?? null,
        previous: data.previous ?? data.prev ?? null,
        count: data.count,
      };

      const items = (payload.results ?? []).map((business, index) => ({
        ...business,
        id: business.id || `b${index + 1}`,
      }));

      return {
        items,
        next: payload.next ?? data.next ?? null,
        previous: payload.previous ?? payload.prev ?? data.previous ?? data.prev ?? null,
        count: payload.count ?? data.count ?? items.length,
      };
    },
    enabled: isAuthenticated && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

// Hook for fetching business details
export const useBusinessDetail = (businessId: string | undefined) => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['business', businessId],
    queryFn: async (): Promise<BusinessDetailData> => {
      if (!businessId) {
        throw new Error('Business ID is required');
      }

      const response = await apiGet(getApiUrl(`${API_ENDPOINTS.ADMIN_BUSINESSES}${businessId}/`));

      if (!response.ok) {
        let message = 'Failed to fetch business details';
        try {
          const body: unknown = await response.json();
          if (isRecord(body)) {
            const fallback = errorMessage(body, message);
            message = typeof body.error === 'string' && body.error
              ? body.error
              : errorMessage(body.error, fallback);
          }
        } catch (parseError) {
          console.error('Error parsing business details API error response:', parseError);
        }
        throw new Error(message);
      }

      const data: BusinessDetailResponse = await response.json();
      return data.data;
    },
    enabled: !!businessId && isAuthenticated,
  });
};
